import { NotificationIcon } from "components/atoms/NotificationIcon";

const timeAgo = (value: string): string => {
  const diffMs = Math.max(0, Date.now() - new Date(value).getTime());
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

export interface NotificationCardViewProps {
  category: string;
  carrierName?: string | null;
  title: string;
  body: string;
  linkUrl?: string | null;
  // Button text for linkUrl; "Learn more" when not set.
  linkLabel?: string | null;
  createdDate?: string | null;
  read?: boolean;
  onClick?: () => void;
  hasDetail?: boolean;
  onViewDetails?: () => void;
}

/**
 * Presentational notification card — the single source of truth for how a
 * notification looks. Used both in the customer bell flyout (interactive, with
 * onClick to mark read) and in the staff compose panel preview (static).
 */
export const NotificationCardView = ({
  category,
  carrierName,
  title,
  body,
  linkUrl,
  linkLabel,
  createdDate,
  read = false,
  onClick,
  hasDetail = false,
  onViewDetails,
}: NotificationCardViewProps) => {
  return (
    <div
      onClick={onClick}
      // Subtle blue tint for unread. Uses a translucent blue rather than a
      // theme blue step because the light theme's low blue steps are dark navy;
      // a low-alpha blue reads as a faint light-blue on white and a subtle tint
      // on the dark dropdown background.
      style={read ? undefined : { backgroundColor: "rgba(0, 145, 255, 0.08)" }}
      className={`flex items-center gap-3 px-4 py-4 ${
        onClick ? "cursor-pointer" : ""
      }`}
    >
      <div className="flex items-center justify-center flex-shrink-0">
        <NotificationIcon
          category={category}
          carrierName={carrierName}
          size={34}
          read={read}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div
            className="font-semibold text-gray12"
            style={{ fontSize: 15, lineHeight: 1.3 }}
          >
            {title || "Untitled notification"}
          </div>
          {!read && (
            <span className="flex-shrink-0 w-2 h-2 mt-1 rounded-full bg-blue9" />
          )}
        </div>
        {/* Secondary: muted grey, ~13px, tight line-height, clamped to 3 lines.
            Theme token (not a fixed hex) so it keeps contrast in dark mode. */}
        <div
          className="mt-1"
          style={{
            fontSize: 13,
            lineHeight: 1.45,
            color: "var(--colorsGray11)",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 3,
            overflow: "hidden",
          }}
        >
          {body}
        </div>
        {hasDetail ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onViewDetails?.();
            }}
            className="inline-flex items-center px-3 py-1.5 mt-2 text-xs font-medium bg-transparent border rounded-md border-gray6 text-gray12 hover:bg-gray4 hover:border-gray9"
          >
            View details
          </button>
        ) : linkUrl ? (
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="inline-flex items-center px-3 py-1.5 mt-2 text-xs font-medium bg-transparent border rounded-md border-gray6 text-gray12 hover:bg-gray4 hover:border-gray9"
          >
            {linkLabel || "Learn more"}
          </a>
        ) : null}
        <div className="mt-1.5 text-xs text-gray10">
          {createdDate ? timeAgo(createdDate) : "Just now"}
        </div>
      </div>
    </div>
  );
};
